import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/types";
import SettingsIcon from "@mui/icons-material/Settings";
import { AppBar, Box, Button, Toolbar, Typography } from "@mui/material";
import { IconButton } from "@mui/material";
import { ReactComponent as SbuxLogo } from "../assets/image.svg";
import SbuxIdleTimer from "../components/SbuxIdleTimer";
import SbuxSelect from "../components/SbuxSelect";
import SbuxSettings from "../components/SbuxSettings";
import {
  CONTACT_RECORDING_START,
  NAVBAR_AGENT_SET_OFFLINE,
  NAVBAR_SIGNOUT_BTN_CLICKED,
  VAR_CONTACT_ID,
  VAR_INITIAL_CONTACT_ID,
  VAR_IS_RECORDING,
  VAR_RECORDING_CONTROLS,
} from "../constants";
import useTextTranslate from "../features/content/hooks/useTextTranslate";
import ScreenRecordingButtons from "../features/content/screenRecording";
import useCss from "../hooks/useCss";
import { signOut } from "../services/auth";
import { getCcpContactStatus, getCcpInitializedStatus } from "../services/ccp";
import { getConnectedInstance } from "../services/instance";
import { updateCotactRecordingStateAction } from "../services/screenRecording";
import { resetTabToCurrent } from "../slices/tabsSlice";
import { screenRecordingActions } from "../slices/screenRecordingSlice";
import { setAgentStateOffline } from "../utils/agentStateChangeUtil";
import NavDrawer from "./NavDrawer";
import styles from "./styles";
import { getCognitoUserIdSelector } from "../selectors/authSelector";
import {
  getConnectedInstanceSelector,
  selectUserInstances,
} from "../selectors/userInstanceSelector";
import {
  getCcpContactStatusSelector,
  getCcpContactAttributesSelector,
  getCcpContactDataSelector,
} from "../selectors/ccpSelector";
import { getTabsStatusSelector } from "../selectors/tabsSelector";
import {
  getContactRecordingStateSelector,
  getContactRecordingLoadingStateSelector,
  getContactRecordingStateErrorSelector,
} from "../selectors/screenRecordingSelector";

const Navbar = () => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();

  const cognitoUserId = useAppSelector(getCognitoUserIdSelector);
  const connectedInstance = useAppSelector(getConnectedInstanceSelector);
  const instances = useAppSelector(selectUserInstances);
  const ccpContactStatus = useAppSelector(getCcpContactStatusSelector);
  const contactAttributes = useAppSelector(getCcpContactAttributesSelector);
  const contactData = useAppSelector(getCcpContactDataSelector);
  const tabsStatus = useAppSelector(getTabsStatusSelector);
  const contactRecordingState = useAppSelector(
    getContactRecordingStateSelector,
  );
  const contactRecordingLoadingState = useAppSelector(
    getContactRecordingLoadingStateSelector,
  );
  const contactRecordingStateError = useAppSelector(
    getContactRecordingStateErrorSelector,
  );

  const [openSettings, setOpenSettings] = useState(false);
  const [defaultInstance, setDefaultInstance] = useState<{
    name: string;
    value: string;
  } | null>(null);
  const [changeInstance, setChangeInstance] = useState<{
    name: string;
    value: string;
  } | null>(null);
  const instanceUrl = connectedInstance ? connectedInstance.instanceUrl : null;
  const signOutTranslatedText = useTextTranslate("Sign Out");
  const isRecordingControlsEnabled =
    connectedInstance?.appConfig?.isRecordingControlsEnabled ?? false;

  const isStopRecordingFeatureImplementedInAWS =
    connectedInstance?.appConfig?.isStopRecordingFeatureImplementedInAWS ??
    false;

  const areScreenRecordingControlsEnabledForCallType =
    (contactAttributes?.[VAR_RECORDING_CONTROLS]?.value ?? "true") === "true" &&
    contactAttributes?.[VAR_INITIAL_CONTACT_ID]?.value &&
    contactAttributes?.[VAR_CONTACT_ID]?.value;

  const updateInitialContactRecordingState = (isRecording: boolean) => {
    dispatch(
      screenRecordingActions.resetContactRecordingStateAction(
        isRecording ? CONTACT_RECORDING_START : null,
      ),
    );
  };

  useEffect(() => {
    const isRecording = contactAttributes?.[VAR_IS_RECORDING]?.value === "true";
    updateInitialContactRecordingState(isRecording);
  }, [contactAttributes]);

  useEffect(() => {
    connectedInstance &&
      connectedInstance.instanceId &&
      setDefaultInstance({
        name: connectedInstance.friendlyName,
        value: connectedInstance.instanceId,
      });
  }, [connectedInstance]);

  const handleSignOut = async (evt?: React.MouseEvent) => {
    evt?.preventDefault();
    await setAgentStateOffline(NAVBAR_SIGNOUT_BTN_CLICKED);
    await signOut({
      instanceUrl: instanceUrl ?? "",
      component: "NavBar",
      userEmail: cognitoUserId ?? "",
    });
  };

  const handleSettings = () => {
    !openSettings && setOpenSettings(true);
  };

  const toggleHandleSettings = () => {
    setOpenSettings(!openSettings);
  };

  const menuItems = () => {
    if (!instances || instances.length === 0) {
      return [];
    }
    const sortedInstances = [...instances].sort((a, b) =>
      a.friendlyName.localeCompare(b.friendlyName),
    );

    return sortedInstances.map((instance) => {
      return {
        name: instance.friendlyName,
        value: instance.instanceId,
      };
    });
  };

  const actionContent = () => (
    <>
      <SbuxSelect
        disabled={
          tabsStatus === "disabled" ||
          instances.length === 1 ||
          (ccpContactStatus === null || ccpContactStatus === "closed"
            ? false
            : true)
        }
        label="Instance"
        menuItems={menuItems()}
        defaultValue={defaultInstance?.value ?? ""}
        value={
          changeInstance ? changeInstance.value : (defaultInstance?.value ?? "")
        }
        handleSelectChange={async (value) => {
          await setAgentStateOffline(NAVBAR_AGENT_SET_OFFLINE);
          const changeInstance = instances.find((f) => f.instanceId === value);
          if (changeInstance) {
            setChangeInstance({
              name: changeInstance.friendlyName,
              value: changeInstance.instanceId,
            });
            window.connect && window.connect.core.terminate();
            await dispatch(getConnectedInstance(changeInstance));
            await dispatch(getCcpInitializedStatus(false));
            await dispatch(getCcpContactStatus(null));

            dispatch(resetTabToCurrent());
          }
        }}
      />

      <Typography variant="h5">{cognitoUserId}</Typography>

      <Button onClick={handleSignOut} variant="outlined" size="medium">
        {signOutTranslatedText}
      </Button>
    </>
  );

  const handleRecordingButtonTrigger = async ({
    action,
    contactId,
    initialContactId,
  }: {
    action: string;
    contactId: string;
    initialContactId: string;
  }) => {
    await dispatch(
      updateCotactRecordingStateAction({ action, contactId, initialContactId }),
    );
  };

  const hideErrorDialog = async () => {
    await new Promise((r) => setTimeout(r, 5000));
    dispatch(screenRecordingActions.resetError());
  };

  return (
    <>
      {defaultInstance && (
        <AppBar position="static" sx={{ width: "100%" }}>
          <Toolbar>
            <SbuxLogo className={classes.sbuxLogo} />

            <Typography
              variant="h5"
              component="div"
              className={classes.appName}
            >
              {`Connect Desktop`}
            </Typography>

            <Box className={classes.recordingButtons}>
              {areScreenRecordingControlsEnabledForCallType &&
                isRecordingControlsEnabled && (
                  <ScreenRecordingButtons
                    handleRecordingButtonTrigger={handleRecordingButtonTrigger}
                    isStopRecordingFeatureImplementedInAWS={
                      isStopRecordingFeatureImplementedInAWS
                    }
                    contactAttributes={contactAttributes}
                    contactData={contactData}
                    ccpContactStatus={ccpContactStatus}
                    contactRecordingState={contactRecordingState}
                    loading={contactRecordingLoadingState ?? false}
                    error={contactRecordingStateError}
                    hideErrorDialog={hideErrorDialog}
                  />
                )}
            </Box>

            <Box className={classes.toolbarActions}>
              {actionContent()}

              <IconButton
                disableFocusRipple
                disableRipple
                disabled={tabsStatus === "disabled" ? true : false}
                onClick={handleSettings}
              >
                <SettingsIcon sx={{ fontSize: 30 }} />
              </IconButton>

              <NavDrawer cognitoUserId={cognitoUserId}>
                {actionContent()}
              </NavDrawer>
            </Box>

            <SbuxIdleTimer />

            {openSettings && (
              <SbuxSettings
                openSettings={openSettings}
                toggleHandleSettings={toggleHandleSettings}
              />
            )}
          </Toolbar>
        </AppBar>
      )}
    </>
  );
};

export default Navbar;
